import { FileText, MonitorSmartphone, WrenchIcon, Settings, Layers, BarChart, AppWindow, Tags, Users, FileSearch, ShieldAlert, ClipboardList, Building2, Shield, SlidersHorizontal, ShieldCheck, ScrollText, Compass, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useWorkspace, type ResolvedNavItem } from "@/workspace/WorkspaceProvider";
import type { NavItemId } from "@/workspace/registry";
import { useCurrentUserRole } from "@/hooks/rbac/useCurrentUserRole";
import { trackUserAction } from "@/services/analyticsService";

/**
 * Sidebar item shape. A `children` array turns the entry into a collapsible
 * group with nested links — keeps related destinations (e.g. Apps + the two
 * configuration surfaces that follow the same domain) under one parent so
 * the top-level list stays scannable.
 *
 * `id` is the registry id (e.g. "nav.devices") when the item came from the
 * workspace, or a synthesized "nav.static.<slug>" for the unscoped fallback.
 * Carrying it through to the click handler lets analytics aggregate clicks
 * by a stable key independent of localized titles.
 */
type NavItem = {
  id: string;
  title: string;
  url: string;
  icon: LucideIcon;
  /** Optional nested links rendered indented under a collapsible parent. */
  children?: NavItem[];
};

type NavSection = { label: string; items: NavItem[] };

/**
 * Icon mapping for workspace registry nav ids. Kept here (presentation layer)
 * so the registry stays free of UI dependencies. Items not in this map fall
 * back to a generic icon so a misconfigured workspace never breaks render.
 */
const NAV_ICONS: Record<NavItemId, LucideIcon> = {
  "nav.gettingStarted": FileText,
  "nav.dashboard": BarChart,
  "nav.devices": MonitorSmartphone,
  "nav.applications": AppWindow,
  "nav.pppc": Shield,
  "nav.appConfig": SlidersHorizontal,
  "nav.configurations": Layers,
  "nav.patching": WrenchIcon,
  "nav.vulnerabilities": ShieldAlert,
  "nav.compliance": ShieldCheck,
  "nav.tags": Tags,
  "nav.usersAndRoles": Users,
  "nav.enrollment": ClipboardList,
  "nav.scripts": ScrollText,
  "nav.logs": FileSearch,
  "nav.settings": Settings,
  "nav.tenants": Building2,
};

// Flat per-section list — used when no role-scoped workspace is in effect (the
// "full console" fallback). When a workspace IS active, we ignore this list
// and render workspace-resolved items grouped by their registry section.
const navSections: NavSection[] = [
  {
    label: "Overview",
    items: [
      { id: "nav.gettingStarted", title: "Getting Started", url: "/getting-started", icon: FileText },
      { id: "nav.dashboard", title: "Dashboard", url: "/analytics", icon: BarChart },
    ],
  },
  {
    label: "Platform",
    items: [
      { id: "nav.devices", title: "Devices", url: "/devices", icon: MonitorSmartphone },
      { id: "nav.applications", title: "Apps", url: "/applications", icon: AppWindow },
      { id: "nav.pppc", title: "Privacy Permissions", url: "/configurations/pppc", icon: Shield },
      { id: "nav.appConfig", title: "App Configuration", url: "/configurations/app-config", icon: SlidersHorizontal },
      { id: "nav.configurations", title: "Configurations", url: "/configurations", icon: Layers },
      { id: "nav.patching", title: "Software Updates", url: "/software-updates", icon: WrenchIcon },
      { id: "nav.vulnerabilities", title: "Vulnerabilities", url: "/vulnerabilities", icon: ShieldAlert },
      { id: "nav.tags", title: "Tags", url: "/tags", icon: Tags },
    ],
  },
  {
    label: "System Administration",
    items: [
      { id: "nav.usersAndRoles", title: "Users & Roles", url: "/admin/users-and-roles", icon: Users },
      { id: "nav.enrollment", title: "Enrollment Profiles", url: "/admin/enrollment-profiles", icon: ClipboardList },
      { id: "nav.logs", title: "Logs", url: "/logs", icon: FileSearch },
      { id: "nav.settings", title: "Settings", url: "/settings", icon: Settings },
    ],
  },
  {
    label: "Tenant Administration",
    items: [
      { id: "nav.tenants", title: "Tenants", url: "/admin/tenants", icon: Building2 },
      // Workspaces nav entry temporarily hidden.
      // { id: "nav.static.workspaces", title: "Workspaces", url: "/admin/workspaces", icon: SlidersHorizontal },
    ],
  },
];

/**
 * Section ordering used when grouping workspace-resolved items. Mirrors the
 * order used by the static fallback so the visual hierarchy is consistent
 * whether a workspace is active or not.
 */
const SECTION_ORDER: ReadonlyArray<NavSection["label"]> = [
  "Overview",
  "Platform",
  "System Administration",
  "Tenant Administration",
];

/**
 * Group workspace-resolved nav items into the same section structure used by
 * the static fallback. Items are assumed to be permission-filtered upstream
 * (`buildEffectiveNav`); this function additionally:
 *   - Routes items whose `section` is unknown/missing into the closest known
 *     bucket ("Platform") so a stale registry entry never goes invisible.
 *   - Sorts items within each section by their workspace-specified `order`
 *     so re-ordering by an admin survives any upstream resort.
 *   - Drops sections that end up empty (no items survived the permission /
 *     registry filtering) so we never render a bare section header.
 */
export function groupWorkspaceNav(items: ResolvedNavItem[]): NavSection[] {
  type Bucketed = NavItem & { __order: number };
  const buckets = new Map<NavSection["label"], Bucketed[]>();

  const isKnownSection = (s: string): s is NavSection["label"] =>
    (SECTION_ORDER as ReadonlyArray<string>).includes(s);

  for (const item of items) {
    const icon = NAV_ICONS[item.id as NavItemId] ?? Layers;
    const sectionLabel: NavSection["label"] = isKnownSection(item.section)
      ? item.section
      : "Platform";
    const bucket = buckets.get(sectionLabel) ?? [];
    bucket.push({ id: item.id, title: item.label, url: item.url, icon, __order: item.order });
    buckets.set(sectionLabel, bucket);
  }

  return SECTION_ORDER
    .map((label) => {
      const raw = buckets.get(label) ?? [];
      const sorted = [...raw]
        .sort((a, b) => a.__order - b.__order)
        .map(({ __order, ...rest }) => rest);
      return { label, items: sorted };
    })
    .filter((section) => section.items.length > 0);
}

export function AppSidebar() {
  const { displayMode, open, isMobile } = useSidebar();
  const location = useLocation();
  const { effectiveNavItems, workspace } = useWorkspace();
  const { data: roleName } = useCurrentUserRole();

  // When a workspace is in effect, render its (already permission-filtered)
  // items. Otherwise fall back to the static full-console list so admins and
  // unscoped users still see everything they're entitled to.
  const usingWorkspace = Boolean(workspace && effectiveNavItems);
  const sections = usingWorkspace
    ? groupWorkspaceNav(effectiveNavItems!)
    : navSections;

  // Routes that should match exactly (not as a prefix) so that sub-routes
  // don't keep the parent item highlighted alongside the child.
  const EXACT_MATCH_ROUTES = new Set(["/configurations", "/applications"]);
  const isActive = (url: string) => {
    if (EXACT_MATCH_ROUTES.has(url)) return location.pathname === url;
    return location.pathname === url || location.pathname.startsWith(url + '/');
  };

  // Don't render content when sidebar is collapsed on DESKTOP only
  if (!open && !isMobile) {
    return (
      <Sidebar className="border-r border-brand-auxiliary1/10 md:mt-16 md:!h-[calc(100svh-4rem)] overflow-hidden transition-all duration-300 ease-out">
        <SidebarContent className="pt-4 md:pt-8" />
      </Sidebar>
    );
  }

  /**
   * Click tracker — fires once per click on a sidebar leaf. Wrapped in
   * try/catch so a flaky analytics backend can never block navigation.
   * The event payload is intentionally small and stable so the backend
   * can group by (role, navId) without parsing localized titles.
   */
  const handleNavClick = (item: NavItem, section: string) => {
    try {
      void trackUserAction("sidebar_nav_click", {
        navId: item.id,
        url: item.url,
        section,
        title: item.title,
        role: roleName ?? null,
        workspaceId: workspace?.id ?? null,
        workspaceName: workspace?.name ?? null,
        source: usingWorkspace ? "workspace" : "static",
        from: location.pathname,
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  const renderLeafButton = (item: NavItem, section: string) => {
    const isInMiniMode = open && displayMode === "mini";
    const active = isActive(item.url);
    const onClick = () => handleNavClick(item, section);

    if (isInMiniMode) {
      return (
        <TooltipProvider key={item.title}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarMenuButton asChild isActive={active}>
                <Link to={item.url} onClick={onClick} className={cn(
                  "w-full justify-center px-2 transition-all duration-150",
                  active && "animate-fade-in border-l-3 border-l-primary bg-primary/10 rounded-md"
                )}>
                  <item.icon
                    className={cn("h-[18px] w-[18px]", active ? "text-primary" : "text-foreground")}
                    strokeWidth={active ? 2.5 : 2}
                  />
                 </Link>
               </SidebarMenuButton>
             </TooltipTrigger>
             <TooltipContent side="right">
               <p>{item.title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <SidebarMenuButton asChild isActive={active}>
        <Link to={item.url} onClick={onClick} className={cn(
          "transition-all duration-150",
          active && "animate-fade-in border-l-3 border-l-primary"
        )}>
          <item.icon className={cn("h-4 w-4", active ? "text-primary" : "text-foreground")} />
          <span className={cn("text-foreground", active && "font-semibold text-primary")}>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    );
  };

  // Empty-state: a role-scoped workspace resolved, but every item it carried
  // was filtered out by RBAC permissions. We don't show this for the
  // unscoped fallback (an unscoped sidebar with zero items would mean a
  // user with literally no permissions, which has bigger problems than a
  // sidebar message). The message is informational, not actionable — the
  // user can't grant themselves permissions from here.
  const isInMiniMode = open && displayMode === "mini";
  const showEmptyState = usingWorkspace && sections.length === 0;

  return (
    <Sidebar className="border-r border-brand-auxiliary1/10 md:mt-16 md:!h-[calc(100svh-4rem)] transition-all duration-200 ease-out">
      <SidebarContent className="pt-4 md:pt-8 px-2 transition-opacity duration-200 ease-out">
        {showEmptyState ? (
          isInMiniMode ? (
            // Mini-mode: a single tooltip-wrapped icon keeps the empty state
            // discoverable without overflowing the narrow rail.
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    role="status"
                    aria-label="No navigation available for your role"
                    className="mx-auto mt-2 flex h-9 w-9 items-center justify-center rounded-md bg-muted/60 text-muted-foreground"
                  >
                    <Compass className="h-4 w-4" aria-hidden />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[220px] text-xs">
                  Your workspace doesn't expose any pages you have permission to see. Ask an admin to adjust your role or workspace.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div
              role="status"
              className="mx-2 mt-2 rounded-md border border-dashed border-border/60 bg-muted/30 p-4 text-center"
            >
              <Compass className="mx-auto h-5 w-5 text-muted-foreground" aria-hidden />
              <p className="mt-2 text-sm font-semibold text-foreground">
                Nothing to show here yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Your workspace{workspace?.name ? ` (${workspace.name})` : ""} doesn't expose any pages you currently have permission to view.
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground/80 leading-relaxed">
                Ask an admin to adjust your role or this workspace's navigation.
              </p>
            </div>
          )
        ) : (
          sections.map((section) => (
            <SidebarGroup key={section.label} className="py-1">
              {!isInMiniMode && (
                <div className="mx-2 mb-1 rounded-sm bg-muted/60 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.label}
                </div>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      {renderLeafButton(item, section.label)}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        )}
      </SidebarContent>
    </Sidebar>
  );
}
